<template>
  <validation-observer v-slot="{ handleSubmit }" tag="form" disabled>
    <ui-modal
      :show="show"
      v-bind="$attrs"
      v-on="$listeners"
      class="location-sublocation-modal"
      size="md"
      @before-close="reset"
    >
      <template #header>
        <div class="location-sublocation-modal__title">
          {{ $t('sublocation.addSublocation') }}
        </div>
      </template>
      <div class="location-sublocation-modal__body">
        <ui-input
          v-model="searchLocationsText"
          bordered
          :placeholder="$t('form.placeholders.locationNameOrId')"
          class="location-sublocation-modal__search-input"
        >
          <template #addonLeft>
            <ui-icon name="loupe" size="17" />
          </template>
        </ui-input>
        <location-sublocation-table
          v-model="form.sublocation"
          :locations="locations"
          :is-loading="loading.search"
          :current-location="location"
          :current-sublocations="sublocations"
          class="location-sublocation-modal__search-table"
        />

        <validation-provider
          tag="div"
          name="Sub-location description"
          mode="passive"
          rules="min:1|max:30"
          v-slot="{ errors }"
          class="location-sublocation-modal__field"
        >
          <ui-input
            v-model="form.description"
            maxlength="30"
            minlength="1"
            label="Sub-location description"
            :error="errors[0]"
            :counter="30"
          />
        </validation-provider>

        <validation-provider
          tag="div"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
          class="location-sublocation-modal__service-icons"
        >
          <location-service-type
            v-model="form.type"
            :type-list="sublocationTypeList"
            :label="$t('sublocation.selectIcon')"
            :error="errors[0]"
            class="is-filled"
            @toggle-icon="toggleType"
          />
        </validation-provider>

        <ui-loading v-if="loading.image" inline />
        <location-service-image
          v-else
          v-model="form.image"
          label="Select or upload image for sub-location"
          class="is-filled"
        />
      </div>
      <template #footer="{ close }">
        <div class="location-sublocation-modal__footer">
          <ui-button
            action
            type="primary"
            size="sm"
            @click.prevent="handleSubmit(() => apply(close))"
          >
            {{ $t('form.buttons.apply') }}
          </ui-button>
        </div>
      </template>
    </ui-modal>
  </validation-observer>
</template>

<script>
import { debounce } from 'lodash';
import axios from 'axios';
import api from '@/api';
import LocationSublocationTable from '@/components/Location/LocationSublocationTable.vue';
import LocationServiceType from '@/components/Location/LocationServiceType.vue';
import LocationServiceImage from '@/components/Location/LocationServiceImage.vue';
import resetMixin from '@/mixins/reset-mixin';

export default {
  name: 'LocationSublocationModal',
  components: {
    LocationSublocationTable,
    LocationServiceType,
    LocationServiceImage,
  },

  mixins: [
    resetMixin(() => ({
      form: {
        type: null,
        image: null,
        hasImage: false,
        sublocation: {},
        description: '',
      },
      searchLocationsText: '',
      locations: [],
      loading: {
        search: false,
        image: false,
      },
    })),
  ],

  model: {
    prop: 'show',
    event: 'input',
  },

  props: {
    location: {
      type: Object,
      required: true,
    },
    sublocations: {
      type: Array,
      required: true,
    },
    currentSublocation: {
      type: Object,
      default: null,
    },
    show: Boolean,
  },

  data() {
    return {
      searchHandle: null,
      sublocationTypeList: [
        {
          name: 'MANAGER',
          icon: require('@/assets/images/sublocations/manager.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.MANAGER'),
        },
        {
          name: 'WAITER',
          icon: require('@/assets/images/sublocations/waiter.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.WAITER'),
        },
        {
          name: 'HOUSE_KEEPING',
          icon: require('@/assets/images/sublocations/house-keeping.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.HOUSE_KEEPING'),
        },
        {
          name: 'CONCIERGE',
          icon: require('@/assets/images/sublocations/concierge.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.CONCIERGE'),
        },
        {
          name: 'VALET',
          icon: require('@/assets/images/sublocations/valet.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.VALET'),
        },
        {
          name: 'THERAPIST',
          icon: require('@/assets/images/sublocations/therapist.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.THERAPIST'),
        },
        {
          name: 'RECEPTION',
          icon: require('@/assets/images/sublocations/reception.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.RECEPTION'),
        },
        {
          name: 'ATTENDANT',
          icon: require('@/assets/images/sublocations/attendant.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.ATTENDANT'),
        },
        {
          name: 'BAR',
          icon: require('@/assets/images/groups/spa-bar.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.BAR'),
        },
        {
          name: 'BEAUTICIAN',
          icon: require('@/assets/images/groups/spa-beautician.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.BEAUTICIAN'),
        },
        {
          name: 'CAFE',
          icon: require('@/assets/images/groups/spa-cafe.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.CAFE'),
        },
        {
          name: 'FITNESS',
          icon: require('@/assets/images/groups/spa-fitness.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.FITNESS'),
        },
        {
          name: 'NAILS',
          icon: require('@/assets/images/groups/spa-nails.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.NAILS'),
        },
        {
          name: 'SALON',
          icon: require('@/assets/images/groups/spa-salon.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.SALON'),
        },
        {
          name: 'SAUNA',
          icon: require('@/assets/images/groups/spa-sauna.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.SAUNA'),
        },
        {
          name: 'YOGA',
          icon: require('@/assets/images/groups/spa-yoga.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.YOGA'),
        },
        {
          name: 'WELLNESS',
          icon: require('@/assets/images/groups/spa-wellness.svg'), // eslint-disable-line global-require
          label: this.$t('sublocation.types.WELLNESS'),
        },
      ],
    };
  },

  computed: {
    locationsSearchParams() {
      let searchParams;

      if (!this.searchLocationsText) {
        searchParams = null;
      } else if (!Number.isNaN(+this.searchLocationsText)) {
        searchParams = {
          payoutId: this.searchLocationsText,
        };
      } else {
        searchParams = {
          locationName: this.searchLocationsText,
        };
      }

      return searchParams;
    },
  },

  watch: {
    searchLocationsText(newText) {
      if (!newText) {
        this.locations = [];
      } else {
        this.loading.search = true;
        this.loadLocations();
      }
    },
    async currentSublocation(sublocation) {
      this.reset();

      if (!sublocation) {
        return;
      }

      this.form = {
        ...this.form,
        type: sublocation.subLocationType,
        hasImage: sublocation.hasImage,
        image: sublocation.image,
        description: sublocation.description,
        sublocation,
      };

      this.locations = [sublocation];

      if (!this.form.image && !this.form.hasImage) {
        this.loading.image = true;

        try {
          const { data } = await api.locations.getSublocationImage({
            locationId: this.location.id,
            sublocationId: sublocation.id,
          });
          this.form.image = data;
          this.form.hasImage = true;
        } catch (e) {
          this.form.hasImage = false;
        } finally {
          this.loading.image = false;
        }
      }
    },
  },

  methods: {
    apply(closeCallback) {
      if (this.form.sublocation && this.form.type) {
        const newSublocation = {
          id: +this.form.sublocation.id,
          name: this.form.sublocation.name,
          payoutId: this.form.sublocation.payoutId,
          subLocationType: this.form.type,
          hasImage: this.form.hasImage,
          image: this.form.image,
          showOrder: this.sublocations.length,
          description: this.form.description,
        };
        if (this.currentSublocation) {
          const index = this.sublocations.findIndex(({ id }) => id === this.currentSublocation.id);
          this.sublocations.splice(index, 1, newSublocation);
        } else {
          this.sublocations.push(newSublocation);
        }
        closeCallback();
      }
    },
    toggleType(name) {
      this.form.type = name;
    },
    loadLocations: debounce(async function loadLocationsDebounced() {
      if (this.searchHandle) {
        this.searchHandle.cancel();
      }

      this.searchHandle = api.notifications.getLocations({
        country: this.location.country,
        ...this.locationsSearchParams,
      });

      try {
        const { data } = await this.searchHandle;

        if (data) {
          this.locations = data.data;
        }
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$showServerError(e);
        }
      } finally {
        this.searchHandle = null;
        this.loading.search = false;
      }
    }, 500),
  },
};
</script>
